/**
 * AEO Impact Dashboard Service
 *
 * API client for fetching AEO impact comparison data.
 */

import { authFetch } from '@/utils/auth-fetch';

// =========================================================================
// Types
// =========================================================================

export interface AEOImpactSummary {
  totalPublished: number;
  totalDeployed: number;
  deploymentRate: number;
  catalogCoverage: number;
  totalCatalog: number;
}

export interface OrganicDailyPoint {
  date: string;
  totalOrganic: number;
  deployedOrganic: number;
  pctOnEnriched: number;
  deployedUrls: number;
}

export interface OrganicImpact {
  daily: OrganicDailyPoint[];
  trend: number | null;
}

export interface GroupMetrics {
  products: number;
  totalImpressions: number;
  totalClicks: number;
  avgCtr: number;
  // null when the group has no impressions behind it: an impression-weighted
  // position is undefined there, and 0 would read as the best possible rank.
  avgPosition: number | null;
}

export interface GSCBucketRow {
  bucket: string;
  group: string;
  products: number;
  totalImpressions: number;
  totalClicks: number;
  avgCtr: number;
  avgPosition: number | null;
}

export interface GSCComparison {
  aggregate: Record<string, GroupMetrics>;
  buckets: GSCBucketRow[];
}

export interface GA4GroupMetrics {
  products: number;
  totalSessions: number;
  avgBounceRate: number;
  avgEngagementRate: number;
  avgTimeOnPage: number;
  totalConversions: number;
  totalRevenue: number;
  revenuePerSession: number;
}

export interface GA4BucketRow {
  bucket: string;
  group: string;
  products: number;
  totalSessions: number;
  avgBounceRate: number;
  avgEngagementRate: number;
  avgTimeOnPage: number;
  totalConversions: number;
  totalRevenue: number;
}

export interface GA4Comparison {
  aggregate: Record<string, GA4GroupMetrics>;
  buckets: GA4BucketRow[];
}

export interface AdsGroupMetrics {
  products: number;
  totalClicks: number;
  totalConversions: number;
  conversionRate: number;
  avgCpc: number;
}

// Per-day state of the Ads rollup over the requested range (task-0bsfrrqqfa step 2).
// Aggregation: ok = refreshed and current; stale = refreshed, newer raw data waiting; pending = never
// refreshed yet; failed = refresh gave up; missing = no Ads sync has covered the day.
// Source (separate): a day is source-incomplete until a SUCCESSFUL Ads sync covered it after its
// last change (its sync is still running, failed, or never succeeded) — its figures may be partial.
export interface AdsCompleteness {
  days: number;
  included: number;  // days whose (possibly stale) refreshed figures are in the totals
  ok: number;
  stale: number;
  pending: number;
  failed: number;
  missing: number;
  expired: number;   // past the raw retention horizon: never in the figures
  sourceIncomplete: number;
  complete: boolean;
  notOk: { date: string; state: 'stale' | 'pending' | 'failed' | 'missing' | 'expired' }[];
  sourceNotComplete: { date: string; reason: 'sync_running' | 'sync_failed' | 'not_covered_by_a_successful_sync' }[];
}

// source: which declared reader served it — 'raw' (legacy bounded reader, until the site is cut
// over) or 'rollup' (the daily summary, which always carries `completeness`).
export type AdsComparison =
  | { source: 'raw'; aggregate: Record<string, AdsGroupMetrics>; qualityDistribution: Record<string, Record<string, number>> }
  | { source: 'rollup'; aggregate: Record<string, AdsGroupMetrics>; qualityDistribution: Record<string, Record<string, number>>; completeness: AdsCompleteness };

export interface AIVisibility {
  available: boolean;
  message: string;
}

export interface AEOImpactData {
  summary: AEOImpactSummary;
  organicImpact: OrganicImpact;
  gscComparison: GSCComparison;
  ga4Comparison: GA4Comparison;
  // adsComparison is served separately by getAdsComparison (GET /api/aeo-impact/:siteId/ads).
  deploymentStatus: Record<string, number>;
  aiVisibility: AIVisibility;
}

// =========================================================================
// Service
// =========================================================================

class AEOImpactService {
  async getImpactData(siteId: string, options: { startDate?: string; endDate?: string } = {}): Promise<AEOImpactData> {
    const params = new URLSearchParams();
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);

    const qs = params.toString();
    const url = `/api/aeo-impact/${siteId}${qs ? `?${qs}` : ''}`;
    const response = await authFetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to fetch AEO impact data (${response.status})`);
    }
    return response.json();
  }

  // Separate from getImpactData: on large stores this is far slower and may come back 503
  // ('ads_comparison_timeout') — that error is thrown with the server's message, never hidden.
  async getAdsComparison(siteId: string, options: { startDate?: string; endDate?: string } = {}): Promise<AdsComparison> {
    const params = new URLSearchParams();
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);

    const qs = params.toString();
    const url = `/api/aeo-impact/${siteId}/ads${qs ? `?${qs}` : ''}`;
    const response = await authFetch(url);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || `Failed to fetch AEO impact Ads comparison (${response.status})`);
    }
    return response.json();
  }
}

export const aeoImpactService = new AEOImpactService();
